import { useListingOrderSlice } from 'app/pages/Account/ListOrder/slice';
import {
  I_DetailTicket,
  I_Order,
  I_Paginator,
  I_PayloadGetListOrder,
  I_Ticket,
} from 'app/pages/Account/ListOrder/slice/types';
import {
  MATERRIAL_COMBO_TYPE,
  MATERRIAL_EVOUCHER_TYPE,
  MATERRIAL_PARK_TYPE,
} from 'app/pages/Checkout/hooks/useCheckout';
import {
  EvoucherViewType,
  TicketInfo,
  TicketType,
} from 'app/pages/NewAccount/layouts/ListTicketLayout';
import { OrderState } from 'app/pages/NewAccount/types';
import { getListOrderApi } from 'app/services/authApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

export type SearchValueType = {
  searchText: string;
  parkId: string;
  date: string;
};

const calculateTotalTicket = (list: I_Ticket[]): number => {
  return list.reduce((total, item) => total + (item.amount || 0), 0);
};

const useListTicket = ({
  orderState,
  isActiveTab,
  setLoading,
  onFinally,
}: {
  orderState: OrderState;
  isActiveTab: boolean;
  setLoading: (loading: boolean) => void;
  onFinally?: () => void;
}) => {
  const dispatch = useDispatch();
  const { actions: listingOrderActions } = useListingOrderSlice();
  const [paginator, setPaginator] = useState<I_Paginator>();
  const [listOrder, setListOrder] = useState<I_Order[]>();
  const isNeededCallApi = useRef(true);
  const [optionsGetListOrder, setOptionsGetListOrder] =
    useState<I_PayloadGetListOrder>({
      page: 1,
      limit: 20,
      search: undefined,
      orderState,
    });

  const search = useCallback(
    (payload: Partial<I_PayloadGetListOrder>) => {
      isNeededCallApi.current = true;
      setOptionsGetListOrder({
        ...optionsGetListOrder,
        ...(payload as I_PayloadGetListOrder),
      });
    },
    [optionsGetListOrder],
  );

  const getListOrder = useCallback(
    async (request: I_PayloadGetListOrder) => {
      try {
        setLoading(true);
        const { data } = await getListOrderApi(request);
        setListOrder(data.data);
        dispatch(
          listingOrderActions.onSetOrderCount(data.orderCount || undefined),
        );
        setPaginator(data.paginator);
      } catch (error) {
      } finally {
        setLoading(false);
        onFinally?.();
      }
    },
    [dispatch, listingOrderActions, setLoading, onFinally],
  );

  const getListOrderOnCancelOrderSuccess = useCallback(() => {
    isNeededCallApi.current = true;
    if (+(paginator?.currentItemCount ?? 0) <= 1) {
      setOptionsGetListOrder(prev => ({
        ...prev,
        page: prev.page > 1 ? prev.page - 1 : 1,
      }));
    } else {
      setOptionsGetListOrder(prev => ({
        ...prev,
      }));
    }
  }, [paginator?.currentItemCount]);

  useEffect(() => {
    if (isActiveTab && isNeededCallApi.current) {
      getListOrder({ ...optionsGetListOrder });
      isNeededCallApi.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsGetListOrder, isActiveTab]);

  // useEffect(() => {
  //   const onFocus = () => {
  //     console.log('focus');

  //     if (isNeededCallApi.current) {
  //       getListOrder({ ...optionsGetListOrder });
  //       isNeededCallApi.current = false;
  //     }
  //   };

  //   window.addEventListener('focus', onFocus);

  //   return () => {
  //     window.removeEventListener('focus', onFocus);
  //   };
  // }, [optionsGetListOrder]);

  const newListTicket: TicketType[] =
    useMemo(() => {
      return listOrder?.map(val => {
        const ticketInfo =
          val.items.length > 0
            ? {
                eventName:
                  (val.items[0]?.park?.name ?? '').toUpperCase() +
                  ' | ' +
                  (val.items[0]?.catalog?.name ?? ''),
                serial: val.items[0].tickets?.[0]?.sapVerifyCode
                  ? val.items[0].tickets?.[0]?.sapVerifyCode
                  : val.items[0].tickets?.[0]?.vgsTicketCode ?? '',
                ticketName: val.items[0].show?.name ?? '',
                useDate: val.items[0].performance?.dateFrom?.datetimeDMY ?? '',
                useHour:
                  (val.items[0].materialType !== MATERRIAL_PARK_TYPE
                    ? val.items[0].performance?.dateFrom?.time
                    : '') ?? '',
                position: val.items[0]?.zone?.name ?? '',
                price: val.items[0].tickets?.[0]?.unitPrice
                  ? +val.items[0].tickets?.[0]?.unitPrice
                  : val.items[0].originalPrice ?? 0,
                imgPath: val.items[0].show?.image?.original ?? '',
                isComboTicket: val.items[0].catalog?.mapVGS === 'S4',
                useDateFrom: val.items[0].date?.datetimeDMY ?? '',
                numberAddDays: val.items[0].product?.versionB2b || 0,
                selectedDate: val.items[0].selectedDate?.datetimeDMY ?? '',
              }
            : undefined;
        const tickets = [];
        const vouchers: EvoucherViewType[] = [];
        const giftsInfo: (TicketInfo & {
          orgTickets: I_DetailTicket[];
          amount: number;
          selectedDate: string;
          higherLevelItem: string;
        })[] = [];
        return {
          ...val,
          _id: val.idHash,
          orderInfo: {
            orderId: `${val.id}`,
            amountOfTicket: val.totalTicket ?? 0,
            orderDate: val.orderdate.datetimeDMY,
            totalDiscountValue: val.totalDiscount ?? 0,
            totalPriceValue:
              val.orderType?.value === 'return'
                ? val.totalReturnCharge ?? 0
                : val.totalPrice,
            payment: val.payment,
          },
          paymentInfo: {
            customerName: val.name ?? '',
            phoneNumber: val.phone ?? '',
            email: val.email ?? '',
          },
          ticketInfo: ticketInfo,
          ticketsInfo: tickets,
          giftsInfo,
          vouchers,
          orderDate: val.orderdate,
          expiredAt: val.expiredAt,
          orderState: val?.orderState,
          subOrderState: val?.subOrderState,
        };
      });
    }, [listOrder]) ?? [];
  return {
    listTicket: newListTicket,
    paginator,
    search,
    getListOrderOnCancelOrderSuccess,
    optionsGetListOrder,
  };
};

export default useListTicket;
